import React, { useEffect, useState } from 'react';
import axios from 'axios';
import './ListSignouts.css';
import Navigation from '../Navbar/Navbar';
import {
  Pagination, Form, InputGroup, Container, Row, Col, Card, Badge, Button,
  ButtonGroup, Dropdown, DropdownButton
} from 'react-bootstrap';
import {
  Search, FileEarmarkPdf, Eye, CheckCircleFill, CircleFill,
  ClipboardCheck, ClipboardX, BoxSeam, Calendar, SortDown,
  Grid3x3, ListUl, Download, Funnel
} from 'react-bootstrap-icons';

const apiUrl = "/api";

const ListSignout = () => {
  const [allSignouts, setAllSignouts] = useState([]);
  const [filteredSignouts, setFilteredSignouts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [sortBy, setSortBy] = useState('date');
  const [sortOrder, setSortOrder] = useState('desc');
  const [viewMode, setViewMode] = useState('table');
  const [loading, setLoading] = useState(true);
  const [loadingAll, setLoadingAll] = useState(false);
  const itemsPerPage = 10;

  // Stats
  const [stats, setStats] = useState({ total: 0, open: 0, pending: 0, closed: 0, totalItems: 0 });

  useEffect(() => {
    if (!localStorage.getItem('token')) {
      window.location.href = '#/login';
      return;
    }
    fetchInitialThenAll();
  }, []);

  useEffect(() => {
    filterAndSort();
  }, [searchTerm, statusFilter, sortBy, sortOrder, allSignouts]);

  const fetchInitialThenAll = () => {
    setLoading(true);
    const tenantId = parseInt(localStorage.getItem('selectedTenant')) || 1;
    const token = localStorage.getItem('token');
    const headers = { Authorization: `Bearer ${token}` };

    // Step 1: Fetch first 10 rows quickly
    axios.get(apiUrl + `/signoutlist?tenant_id=${tenantId}&limit=10`, { headers })
      .then(res => {
        setAllSignouts(res.data);
        calculateStats(res.data);
        setLoading(false);

        // Step 2: Fetch ALL data in background
        setLoadingAll(true);
        return axios.get(apiUrl + `/signoutlist?tenant_id=${tenantId}`, { headers });
      })
      .then(res => {
        if (res) {
          setAllSignouts(res.data);
          calculateStats(res.data);
          setLoadingAll(false);
        }
      })
      .catch(err => {
        console.error(err);
        setLoading(false);
        setLoadingAll(false);
      });
  };

  const calculateStats = (signouts) => {
    const total = signouts.length;
    const open = signouts.filter(s => s.locked === 0 && (!s.approval_status || s.approval_status === 'Rejected' || s.approval_status === 'Approved')).length;
    const pending = signouts.filter(s => s.locked === 0 && s.approval_status === 'Requested').length;
    const closed = signouts.filter(s => s.locked === 1).length;
    const totalItems = signouts.reduce((sum, s) => sum + (parseInt(s.item_count) || 0), 0);
    setStats({ total, open, pending, closed, totalItems });
  };

  const filterAndSort = () => {
    let filtered = [...allSignouts];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(s =>
        s.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
        s.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Status filter
    if (statusFilter === 'open') {
      filtered = filtered.filter(s => s.locked === 0 && (!s.approval_status || s.approval_status === 'Rejected' || s.approval_status === 'Approved'));
    } else if (statusFilter === 'pending') {
      filtered = filtered.filter(s => s.locked === 0 && s.approval_status === 'Requested');
    } else if (statusFilter === 'closed') {
      filtered = filtered.filter(s => s.locked === 1);
    }

    // Sorting
    filtered.sort((a, b) => {
      let comparison = 0;
      switch (sortBy) {
        case 'name':
          comparison = a.name.localeCompare(b.name);
          break;
        case 'date':
          comparison = new Date(b.date) - new Date(a.date);
          break;
        case 'items':
          comparison = (b.item_count || 0) - (a.item_count || 0);
          break;
        case 'status':
          comparison = a.locked - b.locked;
          break;
        default:
          comparison = 0;
      }
      return sortOrder === 'asc' ? -comparison : comparison;
    });

    setFilteredSignouts(filtered);
    setCurrentPage(1);
  };

  const formatRelativeDate = (dateStr) => {
    const date = new Date(dateStr);
    const now = new Date();
    const diffMs = now - date;
    const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

    if (diffDays === 0) return 'Today';
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays} days ago`;
    if (diffDays < 30) return `${Math.floor(diffDays / 7)} weeks ago`;
    return date.toLocaleDateString();
  };

  const exportToCSV = () => {
    const headers = ['ID', 'Name', 'Tenant', 'Date', 'Status', 'Items', 'Returned'];
    const rows = filteredSignouts.map(s => [
      s.id,
      s.name,
      s.tenant_name,
      new Date(s.date).toLocaleDateString(),
      s.locked ? 'Closed' : 'Open',
      s.item_count || 0,
      s.returned_count || 0
    ]);

    const csvContent = [headers.join(','), ...rows.map(r => r.join(','))].join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `signouts-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  // Pagination
  const totalPages = Math.ceil(filteredSignouts.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentSignouts = filteredSignouts.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber) => setCurrentPage(pageNumber);

  const renderPagination = () => {
    if (totalPages <= 1) return null;
    const items = [];
    const maxVisiblePages = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);
    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1);
    }

    items.push(<Pagination.Prev key="prev" disabled={currentPage === 1} onClick={() => handlePageChange(currentPage - 1)} />);
    if (startPage > 1) {
      items.push(<Pagination.Item key={1} onClick={() => handlePageChange(1)}>1</Pagination.Item>);
      if (startPage > 2) items.push(<Pagination.Ellipsis key="start-ellipsis" />);
    }
    for (let page = startPage; page <= endPage; page++) {
      items.push(<Pagination.Item key={page} active={page === currentPage} onClick={() => handlePageChange(page)}>{page}</Pagination.Item>);
    }
    if (endPage < totalPages) {
      if (endPage < totalPages - 1) items.push(<Pagination.Ellipsis key="end-ellipsis" />);
      items.push(<Pagination.Item key={totalPages} onClick={() => handlePageChange(totalPages)}>{totalPages}</Pagination.Item>);
    }
    items.push(<Pagination.Next key="next" disabled={currentPage === totalPages} onClick={() => handlePageChange(currentPage + 1)} />);

    return <Pagination className="justify-content-center mt-3">{items}</Pagination>;
  };

  if (loading) {
    return (
      <div>
        <Navigation />
        <Container className="mt-4">
          <div className="text-center py-5">
            <div className="spinner-border text-primary" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </Container>
      </div>
    );
  }

  return (
    <div>
      <Navigation />
      <Container className="signouts-page page-content-wrapper wide py-4">
        {/* Header */}
        <Row className="mb-4 align-items-center">
          <Col>
            <h1 className="page-title"><ClipboardCheck className="me-3" />Signouts List</h1>
          </Col>
          <Col xs="auto">
            {loadingAll && (
              <span className="me-3 text-muted">
                <span className="spinner-border spinner-border-sm me-1" role="status" />
                Loading all records...
              </span>
            )}
            <Button variant="outline-primary" onClick={exportToCSV}>
              <Download className="me-2" />Export CSV
            </Button>
          </Col>
        </Row>

        {/* Stats Cards */}
        <Row className="mb-4">
          <Col md={3} sm={6} className="mb-3">
            <Card className="stat-card stat-total">
              <Card.Body className="text-center">
                <div className="stat-value">{stats.total}</div>
                <div className="stat-label">Total Signouts</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="stat-card stat-open">
              <Card.Body className="text-center">
                <div className="stat-value">{stats.open}</div>
                <div className="stat-label">Open</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="stat-card stat-pending">
              <Card.Body className="text-center">
                <div className="stat-value">{stats.pending}</div>
                <div className="stat-label">Pending Approval</div>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3} sm={6} className="mb-3">
            <Card className="stat-card stat-closed">
              <Card.Body className="text-center">
                <div className="stat-value">{stats.closed}</div>
                <div className="stat-label">Closed</div>
              </Card.Body>
            </Card>
          </Col>
        </Row>

        {/* Filters Bar */}
        <Row className="mb-4 filter-bar">
          <Col md={4} className="mb-2">
            <InputGroup>
              <InputGroup.Text><Search /></InputGroup.Text>
              <Form.Control
                type="text"
                placeholder="Search by name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </Col>
          <Col md={2} className="mb-2">
            <Form.Select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="all">All Status</option>
              <option value="open">Open</option>
              <option value="pending">Pending Approval</option>
              <option value="closed">Closed</option>
            </Form.Select>
          </Col>
          <Col md={2} className="mb-2">
            <Form.Select value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
              <option value="date">Sort by Date</option>
              <option value="name">Sort by Name</option>
              <option value="items">Sort by Items</option>
              <option value="status">Sort by Status</option>
            </Form.Select>
          </Col>
          <Col md={2} className="mb-2">
            <ButtonGroup className="w-100">
              <Button variant={sortOrder === 'desc' ? 'primary' : 'outline-primary'} onClick={() => setSortOrder('desc')}>Newest</Button>
              <Button variant={sortOrder === 'asc' ? 'primary' : 'outline-primary'} onClick={() => setSortOrder('asc')}>Oldest</Button>
            </ButtonGroup>
          </Col>
          <Col md={2} className="mb-2 text-end">
            <ButtonGroup>
              <Button variant={viewMode === 'table' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('table')} title="Table View">
                <ListUl />
              </Button>
              <Button variant={viewMode === 'cards' ? 'primary' : 'outline-primary'} onClick={() => setViewMode('cards')} title="Card View">
                <Grid3x3 />
              </Button>
            </ButtonGroup>
            <small className="ms-2 text-muted">{filteredSignouts.length} results</small>
          </Col>
        </Row>

        {/* Table View */}
        {viewMode === 'table' && (
          <div className="list-signout-container">
            <table className="signout-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Items</th>
                  <th>Date</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentSignouts.length === 0 ? (
                  <tr><td colSpan="6" className="text-center py-4 text-muted">No signouts found</td></tr>
                ) : currentSignouts.map(signout => (
                  <tr key={signout.id} className={`${signout.locked === 0 ? 'row-open' : 'row-closed'} ${(signout.locked === 0 && (new Date() - new Date(signout.date)) / (1000 * 60 * 60 * 24) > 30) ? 'table-danger' : ''
                    }`}>
                    <td className="id-cell">#{signout.id}</td>
                    <td>
                      <a href={`#/inv/signout/?id=${signout.id}`} className="agent-link">
                        <strong>{signout.name}</strong>
                      </a>
                      <small className="text-muted d-block">{signout.tenant_name}</small>
                      {signout.locked === 0 && (new Date() - new Date(signout.date)) / (1000 * 60 * 60 * 24) > 30 && (
                        <Badge bg="danger" className="mt-1">Overdue</Badge>
                      )}
                    </td>
                    <td className="text-center">
                      {signout.locked === 0 ? (
                        signout.approval_status === 'Requested' ? (
                          <Badge bg="warning" className="status-badge text-dark">
                            <CircleFill className="me-1" size={8} />Pending
                          </Badge>
                        ) : signout.approval_status === 'Approved' ? (
                          <Badge bg="info" className="status-badge text-dark">
                            <CheckCircleFill className="me-1" size={8} />Approved
                          </Badge>
                        ) : (
                          <Badge bg="success" className="status-badge">
                            <CircleFill className="me-1" size={8} />Open
                          </Badge>
                        )
                      ) : (
                        <Badge bg="secondary" className="status-badge">
                          <CheckCircleFill className="me-1" size={10} />Closed
                        </Badge>
                      )}
                    </td>
                    <td className="text-center">
                      <span className="items-count">
                        {signout.returned_count || 0}/{signout.item_count || 0}
                        {signout.returned_count === signout.item_count && signout.item_count > 0 && (
                          <CheckCircleFill className="ms-1 text-success" size={12} />
                        )}
                      </span>
                    </td>
                    <td>
                      <span className="date-display">{formatRelativeDate(signout.date)}</span>
                      <small className="text-muted d-block">{new Date(signout.date).toLocaleDateString()}</small>
                    </td>
                    <td className="text-center">
                      <a href={`#/inv/signout/?id=${signout.id}`} className="btn btn-sm btn-outline-primary me-1" title="View">
                        <Eye />
                      </a>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Card View */}
        {viewMode === 'cards' && (
          <Row>
            {currentSignouts.length === 0 ? (
              <Col className="text-center py-4 text-muted">No signouts found</Col>
            ) : currentSignouts.map(signout => (
              <Col md={4} lg={3} key={signout.id} className="mb-3">
                <Card className={`signout-card ${signout.locked === 0 ? 'card-open' : 'card-closed'}`}>
                  <Card.Body>
                    <div className="d-flex justify-content-between align-items-start mb-2">
                      <Badge bg={signout.locked === 0 ? (signout.approval_status === 'Requested' ? 'warning' : signout.approval_status === 'Approved' ? 'info' : 'success') : 'secondary'}
                        className={signout.approval_status === 'Requested' || signout.approval_status === 'Approved' ? 'text-dark' : ''}>
                        {signout.locked === 0 ? (signout.approval_status === 'Requested' ? 'Pending' : signout.approval_status === 'Approved' ? 'Approved' : 'Open') : 'Closed'}
                      </Badge>
                      <small className="text-muted">#{signout.id}</small>
                    </div>
                    <h5 className="card-title">{signout.name}</h5>
                    <p className="text-muted mb-2">{signout.tenant_name}</p>
                    <div className="d-flex justify-content-between align-items-center mb-3">
                      <span><BoxSeam className="me-1" />{signout.item_count || 0} items</span>
                      <span><Calendar className="me-1" />{formatRelativeDate(signout.date)}</span>
                    </div>
                    <a href={`#/inv/signout/?id=${signout.id}`} className="btn btn-primary btn-sm w-100">
                      View Details
                    </a>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}

        {renderPagination()}
      </Container>
    </div>
  );
};

export default ListSignout;
